import { DbRef } from '@caresend/types';
import { encodeGeo } from '../hashing/geo.js';
import '../environment/crypto/index.js';
import { getPath } from './path.js';

const getPostalCodeByLocation = location => {
  var _a, _b;

  return (_b = (_a = location === null || location === void 0 ? void 0 : location.addressComponents) === null || _a === void 0 ? void 0 : _a.find(entry => {
    var _a;

    return ((_a = entry.types) === null || _a === void 0 ? void 0 : _a[0]) === 'postal_code';
  })) === null || _b === void 0 ? void 0 : _b.long_name;
};
const getCityByLocation = location => {
  var _a, _b;

  return (_b = (_a = location === null || location === void 0 ? void 0 : location.addressComponents) === null || _a === void 0 ? void 0 : _a.find(entry => {
    var _a;

    return ((_a = entry.types) === null || _a === void 0 ? void 0 : _a[0]) === 'locality';
  })) === null || _b === void 0 ? void 0 : _b.long_name;
};
const getCountryByLocation = location => {
  var _a, _b;

  return (_b = (_a = location === null || location === void 0 ? void 0 : location.addressComponents) === null || _a === void 0 ? void 0 : _a.find(entry => {
    var _a;

    return ((_a = entry.types) === null || _a === void 0 ? void 0 : _a[0]) === 'country';
  })) === null || _b === void 0 ? void 0 : _b.long_name;
};
const getCountryISOByLocation = location => {
  var _a, _b;

  return (_b = (_a = location === null || location === void 0 ? void 0 : location.addressComponents) === null || _a === void 0 ? void 0 : _a.find(entry => {
    var _a;

    return ((_a = entry.types) === null || _a === void 0 ? void 0 : _a[0]) === 'country';
  })) === null || _b === void 0 ? void 0 : _b.short_name;
};
const getStreetNameByLocation = location => {
  var _a, _b;

  return (_b = (_a = location === null || location === void 0 ? void 0 : location.addressComponents) === null || _a === void 0 ? void 0 : _a.find(entry => {
    var _a;

    return ((_a = entry.types) === null || _a === void 0 ? void 0 : _a[0]) === 'route';
  })) === null || _b === void 0 ? void 0 : _b.long_name;
};
const getStreetNumberByLocation = location => {
  var _a, _b;

  return (_b = (_a = location === null || location === void 0 ? void 0 : location.addressComponents) === null || _a === void 0 ? void 0 : _a.find(entry => {
    var _a;

    return ((_a = entry.types) === null || _a === void 0 ? void 0 : _a[0]) === 'street_number';
  })) === null || _b === void 0 ? void 0 : _b.long_name;
};
const getStateAbbreviationByLocation = location => {
  var _a, _b;

  return (_b = (_a = location === null || location === void 0 ? void 0 : location.addressComponents) === null || _a === void 0 ? void 0 : _a.find(entry => {
    var _a;

    return ((_a = entry.types) === null || _a === void 0 ? void 0 : _a[0]) === 'administrative_area_level_1';
  })) === null || _b === void 0 ? void 0 : _b.short_name;
};
const getStateByLocation = location => {
  var _a, _b;

  return (_b = (_a = location === null || location === void 0 ? void 0 : location.addressComponents) === null || _a === void 0 ? void 0 : _a.find(entry => {
    var _a;

    return ((_a = entry.types) === null || _a === void 0 ? void 0 : _a[0]) === 'administrative_area_level_1';
  })) === null || _b === void 0 ? void 0 : _b.long_name;
};
const getNeighborhoodByLocation = location => {
  var _a, _b;

  return (_b = (_a = location === null || location === void 0 ? void 0 : location.addressComponents) === null || _a === void 0 ? void 0 : _a.find(entry => {
    var _a;

    return ((_a = entry.types) === null || _a === void 0 ? void 0 : _a[0]) === 'neighborhood';
  })) === null || _b === void 0 ? void 0 : _b.long_name;
};
const getServiceRegionTaskPricing = (serviceRegion, taskID) => {
  var _a, _b;

  return (_b = (_a = serviceRegion === null || serviceRegion === void 0 ? void 0 : serviceRegion.taskPricing[taskID]) === null || _a === void 0 ? void 0 : _a.pay) !== null && _b !== void 0 ? _b : 0;
};
/**
 * Given a location and the ServiceGeohash variable child, will calculate the target precision of the hash
 * for the given childKey and return the precision if it exists
 */

const getTargetPrecision = (location, variablesChild, childKey = 'serviceRegionID') => new Array(10).fill(0).map((_val, i) => i).find(p => {
  var _a;

  const geohash = encodeGeo(location, p);
  const id = (_a = variablesChild === null || variablesChild === void 0 ? void 0 : variablesChild[geohash]) === null || _a === void 0 ? void 0 : _a[childKey];
  return !!id;
});
const isLocationOrigin = location => location.lat === 0 && location.lng === 0;
/**
 * Given a location and the ServiceGeohash variable child, will calculate the geohash
 * for the given childKey and return the geohash if it exists
 */

const getGeohash = (location, precision) => encodeGeo(location, precision);
/**
 * ```
 * `${DbRef.SERVICE_GEOHASHES}/${id}/${childPath}`
 * ```
 */

function getServiceGeohashPath(id, childPath) {
  return getPath(`${DbRef.SERVICE_GEOHASHES}/${id}`, childPath);
}
const geohashPrecisionBounds = {
  precisionMin: 1,
  precisionMax: 7
};
const getServiceGeohashFromLocation = async (location, getValueOnce) => {
  const {
    precisionMin,
    precisionMax
  } = geohashPrecisionBounds;
  let precision = precisionMax;
  let serviceGeohash = null;

  while (!serviceGeohash && precision > precisionMin) {
    const geohashStr = getGeohash(location, precision);
    serviceGeohash = await getValueOnce(getServiceGeohashPath(geohashStr !== null && geohashStr !== void 0 ? geohashStr : ''));
    precision -= 1;
  }

  return serviceGeohash;
};
/**
 * Given a location, will calculate the geohash
 * and return the ServiceRegion object if it exists
 */

const getServiceRegionFromLocation = async (location, variables, getValueOnce) => {
  if (!variables || !(location === null || location === void 0 ? void 0 : location.lat) || !(location === null || location === void 0 ? void 0 : location.lng)) return;
  const {
    serviceRegions
  } = variables;
  const serviceGeohash = await getServiceGeohashFromLocation(location, getValueOnce);
  const serviceRegionID = serviceGeohash === null || serviceGeohash === void 0 ? void 0 : serviceGeohash.serviceRegionID;
  if (!serviceRegionID) return;
  return serviceRegions[serviceRegionID];
};
/**
 * Given a location, will calculate the geohash
 * and return the ServiceRegionGroup object if it exists
 */

const getServiceRegionGroupFromLocation = async (location, variables, getValueOnce) => {
  if (!variables || !(location === null || location === void 0 ? void 0 : location.lat) || !(location === null || location === void 0 ? void 0 : location.lng)) return;
  const {
    serviceRegionGroups
  } = variables;
  const serviceGeohash = await getServiceGeohashFromLocation(location, getValueOnce);
  const serviceRegionGroupID = serviceGeohash === null || serviceGeohash === void 0 ? void 0 : serviceGeohash.serviceRegionGroupID;
  if (!serviceRegionGroupID) return;
  return serviceRegionGroups[serviceRegionGroupID];
};
const getDistanceBetweenLocations = (location1, location2, isMiles) => {
  const {
    lat: lat1,
    lng: lon1
  } = location1;
  const {
    lat: lat2,
    lng: lon2
  } = location2;

  const toRadian = angle => Math.PI / 180 * angle;

  const distance = (a, b) => Math.PI / 180 * (a - b);

  const RADIUS_OF_EARTH_IN_KM = 6371;
  const dLat = distance(lat2, lat1);
  const dLon = distance(lon2, lon1);
  const lat1Rad = toRadian(lat1);
  const lat2Rad = toRadian(lat2); //  Haversine Formula

  const a = Math.pow(Math.sin(dLat / 2), 2) + Math.pow(Math.sin(dLon / 2), 2) * Math.cos(lat1Rad) * Math.cos(lat2Rad);
  const c = 2 * Math.asin(Math.sqrt(a));
  let finalDistance = RADIUS_OF_EARTH_IN_KM * c;

  if (isMiles) {
    const conversionMilesToKmRatio = 1.60934;
    finalDistance /= conversionMilesToKmRatio;
  }

  return Number(finalDistance.toFixed(1));
};

export { getCityByLocation, getCountryByLocation, getCountryISOByLocation, getDistanceBetweenLocations, getGeohash, getNeighborhoodByLocation, getPostalCodeByLocation, getServiceGeohashFromLocation, getServiceGeohashPath, getServiceRegionFromLocation, getServiceRegionGroupFromLocation, getServiceRegionTaskPricing, getStateAbbreviationByLocation, getStateByLocation, getStreetNameByLocation, getStreetNumberByLocation, getTargetPrecision, isLocationOrigin };
